/**
 * NotificationService — runs the pure delivery policy against pending reminders,
 * sends through a channel, and writes an auditable delivery log.
 *
 * A NotificationChannel is the port a real bridge implements (Telegram, Web
 * Push, voice). The in-app channel is local-first and always available offline.
 */

import {
  type ChannelCaps,
  type ChannelId,
  type DeliveryContext,
  type DeliveryRecord,
  type Reminder,
  decideDelivery,
} from '@life-os/app-core';
import type { Clock } from './clock.js';
import { type LocalStore, Collections } from './local-store.js';

export interface NotificationChannel {
  readonly id: ChannelId;
  readonly caps: ChannelCaps;
  send(payload: { readonly title: string; readonly body: string }): Promise<boolean>;
}

/** Local-first in-app channel: no network, full content, records what it showed. */
export class InAppChannel implements NotificationChannel {
  readonly id: ChannelId = 'in_app';
  readonly caps: ChannelCaps = { requiresNetwork: false, allowsFullContent: true };
  readonly shown: { title: string; body: string }[] = [];
  async send(payload: { title: string; body: string }): Promise<boolean> {
    this.shown.push(payload);
    return true;
  }
}

export interface ProcessSummary {
  readonly delivered: number;
  readonly held: number;
  readonly offline: number;
  readonly skipped: number;
  /** Reminder ids that were delivered — caller advances these to 'fired'. */
  readonly deliveredIds: readonly string[];
}

export class NotificationService {
  constructor(
    private readonly store: LocalStore,
    private readonly clock: Clock,
  ) {}

  /**
   * Evaluate each reminder against the policy and channel; deliver eligible ones,
   * log every outcome. Returns counts. Caller advances reminder state on delivery.
   */
  async process(
    reminders: readonly Reminder[],
    ctx: DeliveryContext,
    channel: NotificationChannel,
  ): Promise<ProcessSummary> {
    let delivered = 0, held = 0, offline = 0, skipped = 0;
    const deliveredIds: string[] = [];
    for (const r of reminders) {
      const decision = decideDelivery(r, ctx, channel.caps);
      let outcome: DeliveryRecord['outcome'];
      let redacted = false;
      let detail = '';
      switch (decision.kind) {
        case 'deliver': {
          redacted = decision.redacted;
          const ok = await channel.send({ title: r.label, body: redacted ? '(private)' : r.label });
          outcome = ok ? 'delivered' : 'failed';
          if (ok) { delivered++; deliveredIds.push(String(r.id)); }
          break;
        }
        case 'hold_quiet_hours':
          outcome = 'held_quiet_hours';
          detail = `held until ${decision.untilIso}`;
          held++;
          break;
        case 'offline':
          outcome = 'offline';
          offline++;
          break;
        case 'not_due':
        case 'skip':
          continue; // nothing to log for not-yet-due or already-handled reminders
      }
      const at = this.clock.nowIso();
      const rec: DeliveryRecord = {
        id: `${String(r.id)}@${at}@${channel.id}`,
        reminderId: String(r.id),
        channel: channel.id,
        outcome,
        redacted,
        at,
        detail,
      };
      await this.store.put(Collections.deliveries, rec.id, rec);
      if (outcome !== 'delivered' && outcome !== 'held_quiet_hours' && outcome !== 'offline') skipped++;
    }
    return { delivered, held, offline, skipped, deliveredIds };
  }

  async log(): Promise<readonly DeliveryRecord[]> {
    const all = await this.store.list<DeliveryRecord>(Collections.deliveries);
    return [...all].sort((a, b) => (a.at < b.at ? 1 : -1));
  }
}
